import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { Volume2, VolumeX, AlertTriangle, Thermometer, Baby, FileText, Heart, Activity } from 'lucide-react';
import { playBellSound, BELL_LABEL, BELL_ICON } from '../utils/bellSounds';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Custom uploaded bell sound
const BELL_SOUND_URL = 'https://customer-assets.emergentagent.com/job_bfad35dd-1d63-4524-ae59-d9c9812969d6/artifacts/klfvg78y_freesound_community-doorbell1-93978.mp3';

// Helper to check if patient is a child (under 12 years)
const isChild = (age) => {
  if (!age) return false;
  const ageStr = age.toString().toLowerCase();
  if (ageStr.includes('d') || ageStr.includes('m')) return true;
  const years = parseFloat(ageStr);
  return !isNaN(years) && years < 12;
};

const TVDisplay = () => {
  const [currentPatient, setCurrentPatient] = useState(null);
  const [nextPatient, setNextPatient] = useState(null);
  const [upcomingTokens, setUpcomingTokens] = useState([]); // up to 2 next tokens (excluding in-cabin)
  const [suggestedNextPatient, setSuggestedNextPatient] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [onDutyDoctor, setOnDutyDoctor] = useState(null);
  const [blinkCount, setBlinkCount] = useState(0);
  const [isBlinking, setIsBlinking] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [announcement1, setAnnouncement1] = useState('Welcome to K.M Multi-Speciality Clinic');
  const [announcement2, setAnnouncement2] = useState('Please wait for your token number');
  const [scroll1Enabled, setScroll1Enabled] = useState(true);
  const [scroll2Enabled, setScroll2Enabled] = useState(true);
  
  // Promo content state
  const [promoContent, setPromoContent] = useState([]);
  const [showPromo, setShowPromo] = useState(false);
  const [currentPromoIndex, setCurrentPromoIndex] = useState(0);
  const [idleSeconds, setIdleSeconds] = useState(0);
  
  // TV Settings state
  const [promoDelayAfterToken, setPromoDelayAfterToken] = useState(10);
  const [promoDelayNoPatient, setPromoDelayNoPatient] = useState(5);
  const [showDoctorPhoto, setShowDoctorPhoto] = useState(true);
  const [noPatientIdleSeconds, setNoPatientIdleSeconds] = useState(0);
  const [walkinCounters, setWalkinCounters] = useState({ walk_in: 0, phone: 0, attender: 0 });
  
  // Doctor bell alert state (CBG / Temp / Attention)
  const [activeBell, setActiveBell] = useState(null);
  const bellStopRef = useRef(null);
  const seenBellIdRef = useRef(null);
  
  const lastPatientIdRef = useRef(null);
  const lastPatientChangeTimeRef = useRef(Date.now());
  const noPatientStartTimeRef = useRef(null);
  const audioRef = useRef(null);
  // Wake Lock — keeps Android/Silk TV screen ON forever
  const wakeLockRef = useRef(null);
  const noSleepVideoRef = useRef(null);

  // Keep-Awake: prevent TV from sleeping during long idle.
  // Strategy: 1) Screen Wake Lock API (modern browsers & WebView 84+)
  //           2) Fallback — looping muted invisible video (NoSleep technique) for older WebView/Silk
  useEffect(() => {
    let cancelled = false;

    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          const lock = await navigator.wakeLock.request('screen');
          if (cancelled) {
            try { await lock.release(); } catch (_) {}
            return;
          }
          wakeLockRef.current = lock;
          lock.addEventListener('release', () => {
            wakeLockRef.current = null;
          });
          console.log('Screen Wake Lock acquired');
        } else {
          console.log('Wake Lock API not supported — using video fallback');
        }
      } catch (err) {
        console.warn('Wake Lock request failed:', err);
      }
    };

    // Tiny base64 MP4 (1px transparent, 1s, ~600 bytes) — keeps Android WebView/Silk awake
    const NOSLEEP_DATA_URL = 'data:video/mp4;base64,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';

    const startNoSleepVideo = () => {
      try {
        if (noSleepVideoRef.current) {
          const v = noSleepVideoRef.current;
          v.setAttribute('muted', '');
          v.setAttribute('playsinline', '');
          v.muted = true;
          v.loop = true;
          v.playsInline = true;
          v.src = NOSLEEP_DATA_URL;
          const playPromise = v.play();
          if (playPromise && playPromise.catch) {
            playPromise.catch(() => {/* will retry on first interaction */});
          }
        }
      } catch (e) { console.warn('NoSleep fallback failed:', e); }
    };

    requestWakeLock();
    startNoSleepVideo();

    // Re-acquire lock when tab becomes visible again (some Android WebViews release on suspend)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && !wakeLockRef.current) {
        requestWakeLock();
      }
      if (document.visibilityState === 'visible' && noSleepVideoRef.current && noSleepVideoRef.current.paused) {
        startNoSleepVideo();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibility);
      try {
        if (wakeLockRef.current) {
          wakeLockRef.current.release();
          wakeLockRef.current = null;
        }
      } catch (_) {}
      try {
        if (noSleepVideoRef.current) {
          noSleepVideoRef.current.pause();
          noSleepVideoRef.current.removeAttribute('src');
          noSleepVideoRef.current.load();
        }
      } catch (_) {}
    };
  }, []);

  // Helper to construct proper URL for uploaded files
  const getProperUrl = (url) => {
    if (!url) return null;
    
    let fixedUrl = url;
    
    // If URL already starts with /api/uploads, prepend the backend URL
    if (url.startsWith('/api/uploads')) {
      fixedUrl = `${BACKEND_URL}${url}`;
    }
    // If URL starts with /uploads (old format), convert to /api/uploads
    else if (url.startsWith('/uploads')) {
      fixedUrl = `${BACKEND_URL}/api${url}`;
    }
    // If URL contains old/different preview URLs with /uploads/, extract and fix the path
    else if (url.includes('/uploads/')) {
      const match = url.match(/\/uploads\/.+$/);
      if (match) {
        fixedUrl = `${BACKEND_URL}/api${match[0]}`;
      }
    }
    // Return as-is for external URLs (like YouTube)
    
    console.log('getProperUrl:', url, '->', fixedUrl);
    return fixedUrl;
  };

  // Initialize audio
  useEffect(() => {
    audioRef.current = new Audio(BELL_SOUND_URL);
    audioRef.current.volume = 1.0;
    audioRef.current.preload = 'auto';
  }, []);

  // Play bell sound ONCE
  const playNotificationSound = () => {
    if (soundEnabled && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(e => console.log('Audio play failed:', e));
    }
  };

  // Trigger alert: bell once, blink 3 times
  const triggerAlert = () => {
    playNotificationSound();
    setBlinkCount(0);
    setIsBlinking(true);
    // Reset idle timer and hide promo when new patient
    setIdleSeconds(0);
    setShowPromo(false);
    lastPatientChangeTimeRef.current = Date.now();
  };

  // Handle blinking 3 times
  useEffect(() => {
    if (isBlinking && blinkCount < 6) {
      const timer = setTimeout(() => {
        setBlinkCount(prev => prev + 1);
      }, 400);
      return () => clearTimeout(timer);
    } else if (blinkCount >= 6) {
      setIsBlinking(false);
      setBlinkCount(0);
    }
  }, [isBlinking, blinkCount]);

  // Fetch promo content
  const fetchPromoContent = async () => {
    try {
      const response = await axios.get(`${API}/public/promo-content`);
      const activePromos = response.data.filter(p => p.is_active);
      setPromoContent(activePromos);
    } catch (error) {
      console.error('Error fetching promo content:', error);
    }
  };

  // Fetch TV settings
  const fetchTVSettings = async () => {
    try {
      const response = await axios.get(`${API}/public/tv-settings`);
      setPromoDelayAfterToken(response.data.promo_delay_after_token || 10);
      setPromoDelayNoPatient(response.data.promo_delay_no_patient || 5);
      setShowDoctorPhoto(response.data.show_doctor_photo !== false);
    } catch (error) {
      console.error('Error fetching TV settings:', error);
    }
  };

  // Fetch announcements
  const fetchAnnouncements = async () => {
    try {
      const response = await axios.get(`${API}/public/announcements`);
      if (response.data.line1) setAnnouncement1(response.data.line1);
      if (response.data.line2) setAnnouncement2(response.data.line2);
      if (response.data.scroll1_enabled !== undefined) setScroll1Enabled(response.data.scroll1_enabled);
      if (response.data.scroll2_enabled !== undefined) setScroll2Enabled(response.data.scroll2_enabled);
    } catch (error) {
      console.error('Error fetching announcements:', error);
    }
  };

  const fetchDisplayData = async () => {
    try {
      const queueResponse = await axios.get(`${API}/public/queue`);
      const patients = queueResponse.data;

      // Walk-in fairness counters
      try {
        const wc = await axios.get(`${API}/public/walkin-counters`);
        setWalkinCounters(wc.data || { walk_in: 0, phone: 0, attender: 0 });
      } catch (_) {}
      
      const inCabinPatient = patients.find(p => p.status === 'in_cabin');
      // If multiple queued_next exist, pick the oldest (FIFO)
      const queuedNextPatient = patients
        .filter(p => p.status === 'queued_next')
        .sort((a, b) => (a.queued_at || '').localeCompare(b.queued_at || ''))[0];
      
      if (inCabinPatient && inCabinPatient.id !== lastPatientIdRef.current) {
        triggerAlert();
        lastPatientIdRef.current = inCabinPatient.id;
        lastPatientChangeTimeRef.current = Date.now();
        // New patient came in, reset promo state
        setShowPromo(false);
        setIdleSeconds(0);
        // Also clear no-patient timer so it restarts fresh when this patient leaves
        noPatientStartTimeRef.current = null;
        setNoPatientIdleSeconds(0);
      } else if (!inCabinPatient && lastPatientIdRef.current !== null) {
        // Patient just left cabin - reset token idle timer but preserve no-patient timer
        lastPatientIdRef.current = null;
        setIdleSeconds(0);
        // Don't reset showPromo here - let the no-patient timer control it
      }
      
      setCurrentPatient(inCabinPatient);
      setNextPatient(queuedNextPatient);

      // Build "up next" list (top 2 tokens after current in cabin).
      // Order: queued_next first (if any), then waiting patients in token-number order.
      const tokenSortVal = (p) => {
        const t = (p.token_number || '').toUpperCase();
        const m = t.match(/^([A-Z]+)-?(\d+)$/);
        if (!m) return [9, 9999];
        const grp = ['A','G','W','O'].includes(m[1]) ? 0 : (m[1] === 'P' ? 1 : 2);
        return [grp, parseInt(m[2], 10)];
      };
      const queuedNexts = patients
        .filter(p => p.status === 'queued_next')
        .sort((a, b) => (a.queued_at || '').localeCompare(b.queued_at || ''));
      const waitingSorted = patients
        .filter(p => p.status === 'waiting')
        .sort((a, b) => {
          const [ag, an] = tokenSortVal(a);
          const [bg, bn] = tokenSortVal(b);
          return ag - bg || an - bn;
        });
      const merged = [...queuedNexts, ...waitingSorted].filter(p => !inCabinPatient || p.id !== inCabinPatient.id);
      setUpcomingTokens(merged.slice(0, 2));

      // Fetch suggested next patient for auto-call display
      try {
        const suggestResponse = await axios.get(`${API}/patients/suggest-next-public`);
        if (suggestResponse.data.has_next) {
          setSuggestedNextPatient(suggestResponse.data.patient);
        } else {
          setSuggestedNextPatient(null);
        }
      } catch (error) {
        // Silently fail - this is optional
        setSuggestedNextPatient(null);
      }

      try {
        const doctorResponse = await axios.get(`${API}/doctors/on-duty`);
        setOnDutyDoctor(doctorResponse.data);
      } catch (error) {
        console.error('Error fetching on-duty doctor:', error);
      }
    } catch (error) {
      console.error('Error fetching display data:', error);
    }
  };

  // Idle timer - check if same token for configurable seconds
  useEffect(() => {
    const idleTimer = setInterval(() => {
      if (currentPatient && promoContent.length > 0) {
        const elapsed = Math.floor((Date.now() - lastPatientChangeTimeRef.current) / 1000);
        setIdleSeconds(elapsed);
        
        if (elapsed >= promoDelayAfterToken && !showPromo) {
          setShowPromo(true);
        }
      }
    }, 1000);

    return () => clearInterval(idleTimer);
  }, [currentPatient, promoContent.length, showPromo, promoDelayAfterToken]);

  // No patient idle timer - show promos after configurable delay when no patient
  useEffect(() => {
    const noPatientTimer = setInterval(() => {
      if (!currentPatient && promoContent.length > 0) {
        if (noPatientStartTimeRef.current === null) {
          noPatientStartTimeRef.current = Date.now();
        }
        const elapsed = Math.floor((Date.now() - noPatientStartTimeRef.current) / 1000);
        setNoPatientIdleSeconds(elapsed);
        
        if (elapsed >= promoDelayNoPatient && !showPromo) {
          setShowPromo(true);
        }
      }
      // NOTE: do NOT reset showPromo here while a patient is in cabin —
      // that's handled inside fetchDisplayData only when a NEW patient actually arrives.
      // Otherwise this tick would kill the promo every second the idle-token timer turns it on.
    }, 1000);

    return () => clearInterval(noPatientTimer);
  }, [currentPatient, promoContent.length, showPromo, promoDelayNoPatient]);

  // Cycle through promo content using individual durations
  useEffect(() => {
    if (showPromo && promoContent.length > 0) {
      const promo = promoContent[currentPromoIndex];
      const currentDuration = (promo?.duration || 30) * 1000; // Convert to ms
      const promoTimer = setTimeout(() => {
        setCurrentPromoIndex(prev => (prev + 1) % promoContent.length);
      }, currentDuration);
      return () => clearTimeout(promoTimer);
    }
  }, [showPromo, currentPromoIndex, promoContent]);

  useEffect(() => {
    fetchDisplayData();
    fetchAnnouncements();
    fetchPromoContent();
    fetchTVSettings();
    const interval = setInterval(fetchDisplayData, 2000);
    const announcementInterval = setInterval(fetchAnnouncements, 30000);
    const promoInterval = setInterval(fetchPromoContent, 60000);
    const settingsInterval = setInterval(fetchTVSettings, 30000);
    const clockInterval = setInterval(() => setCurrentTime(new Date()), 30000); // throttled to 30s (TV only shows HH:MM)
    // Bell-call polling (every 3s)
    const fetchBell = async () => {
      try {
        const r = await axios.get(`${API}/public/bell-latest`);
        const call = r.data || null;
        if (call && call.id !== seenBellIdRef.current) {
          seenBellIdRef.current = call.id;
          setActiveBell(call);
          if (bellStopRef.current) bellStopRef.current();
          bellStopRef.current = playBellSound(call.type);
          // Auto-clear visual overlay after 8s (sound stops on its own)
          setTimeout(() => setActiveBell((cur) => (cur && cur.id === call.id ? null : cur)), 8000);
        } else if (!call && activeBell) {
          // Server says ack'd — silence locally too
          if (bellStopRef.current) bellStopRef.current();
          bellStopRef.current = null;
          setActiveBell(null);
        }
      } catch (_) { /* ignore */ }
    };
    fetchBell();
    const bellInterval = setInterval(fetchBell, 3000);
    return () => {
      clearInterval(interval);
      clearInterval(announcementInterval);
      clearInterval(promoInterval);
      clearInterval(settingsInterval);
      clearInterval(clockInterval);
      clearInterval(bellInterval);
      if (bellStopRef.current) bellStopRef.current();
    };
  }, []);

  // Check if it's after 9 PM - show closing message
  const currentHour = currentTime.getHours();
  const isClosingTime = currentHour >= 21;

  // Determine if currently in "blink on" state
  const showGreenBlink = isBlinking && blinkCount % 2 === 1;

  const currentPromo = promoContent[currentPromoIndex];

  return (
    <div className="h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex flex-col overflow-hidden">
      {/* Doctor Bell Alert — full-width overlay across the top */}
      {activeBell && (
        <div
          className="fixed inset-x-0 top-0 z-[200] px-4 py-3 flex items-center justify-center gap-4 animate-pulse shadow-2xl"
          data-testid="tv-bell-overlay"
          data-bell-type={activeBell.type}
          style={{
            background: activeBell.type === 'cbg'
              ? 'linear-gradient(135deg,#fde047,#f59e0b)'
              : activeBell.type === 'temp'
              ? 'linear-gradient(135deg,#fdba74,#dc2626)'
              : 'linear-gradient(135deg,#fca5a5,#7f1d1d)',
            color: 'white',
            borderBottom: '6px solid #fef3c7',
          }}
        >
          <div className="text-6xl drop-shadow">{BELL_ICON[activeBell.type] || '🛎️'}</div>
          <div className="font-black uppercase tracking-widest drop-shadow" style={{ fontSize: 'clamp(28px, 4vw, 56px)' }}>
            Doctor is calling — {BELL_LABEL[activeBell.type] || 'Attention'}
          </div>
        </div>
      )}
      
      {/* MAIN CONTENT AREA */}
      <div className="flex-1 flex flex-col relative overflow-hidden">
        
        {/* Show Promo Content after idle delay */}
        {showPromo && currentPromo ? (
          <div className="flex-1 flex flex-col bg-black overflow-hidden">
            {/* Promo header — clinic + doctor on left, BIG current/next tokens on right (so patients can read them during ads) */}
            <div
              className="bg-gradient-to-r from-red-950 to-red-900 px-4 flex items-center justify-between flex-shrink-0 text-white"
              style={{ paddingTop: 6, paddingBottom: 6, minHeight: 'clamp(80px, 12vh, 140px)' }}
            >
              <div className="flex items-center gap-4 min-w-0">
                <img
                  src="https://customer-assets.emergentagent.com/job_bfad35dd-1d63-4524-ae59-d9c9812969d6/artifacts/3d9rjs08_ChatGPT%20Image%20May%2024%2C%202026%2C%2001_38_48%20AM.png"
                  alt="K.M"
                  style={{ height: 'clamp(56px, 9vh, 110px)', width: 'auto', objectFit: 'contain', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.4))' }}
                />
                <div className="min-w-0">
                  <p className="font-bold text-yellow-300 leading-tight" style={{ fontSize: 'clamp(16px, 2.4vh, 26px)' }}>
                    {onDutyDoctor?.name || 'Doctor'}
                  </p>
                  {onDutyDoctor?.qualification && (
                    <p className="text-blue-200 leading-tight" style={{ fontSize: 'clamp(12px, 1.6vh, 18px)' }}>
                      {onDutyDoctor.qualification}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-5">
                {currentPatient ? (
                  <>
                    <div className="flex flex-col items-center justify-center bg-white/10 rounded-xl border-2 border-yellow-400 px-4 py-1" style={{ minWidth: 'clamp(120px, 16vw, 220px)' }}>
                      <span className="text-green-300 font-bold uppercase tracking-widest leading-none" style={{ fontSize: 'clamp(12px, 1.6vh, 18px)' }}>Now Serving</span>
                      <span
                        className="font-black text-yellow-300 leading-none mt-1"
                        style={{ fontFamily: 'Arial Black, sans-serif', fontSize: 'clamp(48px, 8vh, 96px)', textShadow: '0 3px 6px rgba(0,0,0,0.45)' }}
                        data-testid="tv-promo-current-token"
                      >
                        {currentPatient.token_number}
                      </span>
                    </div>
                    {nextPatient && (
                      <div className="flex flex-col items-center justify-center px-3 py-1" style={{ minWidth: 'clamp(90px, 11vw, 150px)' }}>
                        <span className="text-blue-200 font-bold uppercase tracking-widest leading-none" style={{ fontSize: 'clamp(11px, 1.5vh, 16px)' }}>Next</span>
                        <span
                          className="font-black text-white/90 leading-none mt-1"
                          style={{ fontFamily: 'Arial Black, sans-serif', fontSize: 'clamp(28px, 4.5vh, 54px)' }}
                        >
                          {nextPatient.token_number}
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <span className="font-semibold text-green-300" style={{ fontSize: 'clamp(16px, 2.2vh, 26px)' }}>Waiting for next patient…</span>
                )}
              </div>
            </div>
            
            {/* Promo Content - Maximum screen space */}
            <div className="flex-1 flex items-center justify-center bg-black overflow-hidden min-h-0">
              {currentPromo.type === 'video' ? (
                <video
                  src={getProperUrl(currentPromo.url)}
                  autoPlay
                  muted
                  loop
                  className="max-w-full max-h-full object-contain"
                  style={{ maxHeight: '100%', maxWidth: '100%' }}
                />
              ) : (
                <img
                  src={getProperUrl(currentPromo.url)}
                  alt={currentPromo.title || 'Promotional content'}
                  className="max-w-full max-h-full object-contain"
                  style={{ maxHeight: '100%', maxWidth: '100%' }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
            </div>
            
            {/* Minimal promo dots */}
            {promoContent.length > 1 && (
              <div className="flex justify-center items-center gap-1.5 py-0.5 bg-black flex-shrink-0">
                {promoContent.map((promo, idx) => (
                  <div
                    key={promo.id || idx}
                    className={`w-1.5 h-1.5 rounded-full ${idx === currentPromoIndex ? 'bg-green-500' : 'bg-slate-700'}`}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          /* Normal Token Display — Two-half layout: LEFT (clinic/doctor/time) | RIGHT (current token) */
          <div
            className="flex-1 flex min-h-0 overflow-hidden"
            style={{
              background:
                'radial-gradient(circle at 20% 20%, rgba(30,58,138,0.10), transparent 45%), radial-gradient(circle at 80% 80%, rgba(22,163,74,0.10), transparent 50%), linear-gradient(135deg, #f8fafc 0%, #ecfdf5 100%)',
            }}
          >
            {/* Local keyframes — slow rotating ring (matches Login page) */}
            <style>{`
              @keyframes tvLogoRing {
                0%   { transform: rotate(0deg);   opacity: 0.35; }
                50%  { opacity: 0.55; }
                100% { transform: rotate(360deg); opacity: 0.35; }
              }
              @keyframes tvLogoFloat {
                0%, 100% { transform: translateY(0) scale(1); }
                50%      { transform: translateY(-6px) scale(1.012); }
              }
              .tv-logo-ring  { animation: tvLogoRing 180s linear infinite; will-change: transform; }
              .tv-logo-float { animation: tvLogoFloat 10s ease-in-out infinite; will-change: transform; }
              @keyframes paeds-float {
                0%, 100% { transform: translateY(0) rotate(-3deg) scale(1); }
                50%      { transform: translateY(-10px) rotate(3deg) scale(1.03); }
              }
              @keyframes paeds-bounce {
                0%, 100% { transform: scale(1) rotate(-3deg); }
                50%      { transform: scale(1.12) rotate(3deg); }
              }
              @media (prefers-reduced-motion: reduce) {
                .tv-logo-ring, .tv-logo-float { animation: none !important; }
              }
            `}</style>

            {/* Sound Toggle — small, low-contrast, tucked in bottom-right corner */}
            <div className="absolute bottom-2 right-2 z-10 opacity-25 hover:opacity-100 transition-opacity">
              <Button
                onClick={() => setSoundEnabled(!soundEnabled)}
                variant="ghost"
                size="icon"
                className={`h-7 w-7 ${soundEnabled ? 'text-emerald-700' : 'text-slate-400'}`}
                data-testid="tv-sound-toggle"
              >
                {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
              </Button>
            </div>

            {/* LEFT HALF — Logo with rotating ring + Doctor info (stays calm during token-call flash) */}
            <div
              className="w-1/2 flex flex-col px-8 lg:px-12 py-8 border-r-4 relative"
              data-testid="tv-left-panel"
              style={{
                borderRightColor: 'rgba(30,58,138,0.15)',
                background: 'linear-gradient(180deg, rgba(255,255,255,0.85) 0%, rgba(236,253,245,0.6) 100%)',
              }}
            >
              {/* Upper half — Logo with rotating brand ring (made bigger per user request) */}
              <div className="relative flex items-center justify-center" style={{ height: '62%', minHeight: 0, paddingBottom: 8 }}>
                {/* Decorative rotating ring */}
                <div
                  aria-hidden
                  className="tv-logo-ring absolute pointer-events-none"
                  style={{
                    width: '96%', aspectRatio: '1/1',
                    border: '1px dashed rgba(30,58,138,0.18)',
                    borderRadius: '50%',
                    background: 'conic-gradient(from 0deg, rgba(22,163,74,0.08), rgba(30,58,138,0.12), rgba(22,163,74,0.08))',
                  }}
                />
                <img
                  src="https://customer-assets.emergentagent.com/job_bfad35dd-1d63-4524-ae59-d9c9812969d6/artifacts/arabbjev_C0FE19D6-1205-41E8-8EF1-C29C54659CCC.png"
                  alt="K.M Multi Speciality Clinic"
                  data-testid="tv-clinic-logo"
                  className="tv-logo-float relative"
                  style={{
                    maxWidth: '94%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    filter: 'saturate(1.18) contrast(1.08) drop-shadow(0 10px 26px rgba(30,58,138,0.22))',
                    willChange: 'transform',
                  }}
                />
              </div>

              {/* Lower half — Doctor block (centered) */}
              <div className="flex flex-col items-center justify-center gap-2 text-center" style={{ height: '38%', minHeight: 0, paddingTop: 8 }}>
                <p
                  className="font-black leading-tight bg-clip-text"
                  style={{
                    color: 'transparent',
                    backgroundImage: 'linear-gradient(135deg, #1e3a8a 0%, #16a34a 100%)',
                    WebkitBackgroundClip: 'text',
                    fontSize: 'clamp(28px, 4.4vh, 56px)',
                  }}
                >
                  {onDutyDoctor?.name || 'Doctor'}
                </p>
                {onDutyDoctor?.qualification && (
                  <p className="font-bold" style={{ color: '#1e3a8a', fontSize: 'clamp(16px, 2.4vh, 30px)' }}>
                    {onDutyDoctor.qualification}
                  </p>
                )}
                {onDutyDoctor?.info && (
                  <p className="text-slate-600" style={{ fontSize: 'clamp(13px, 1.8vh, 22px)' }}>
                    {onDutyDoctor.info}
                  </p>
                )}
                <span
                  className="inline-flex items-center gap-1.5 mt-1 px-3 py-1 text-white font-bold rounded-full shadow"
                  style={{
                    background: 'linear-gradient(135deg, #1e3a8a 0%, #16a34a 100%)',
                    fontSize: 'clamp(12px, 1.6vh, 18px)',
                    boxShadow: '0 4px 14px rgba(30,58,138,0.30)',
                  }}
                >
                  <span className="inline-block w-2 h-2 rounded-full bg-emerald-300 animate-pulse" />
                  ON DUTY
                </span>
              </div>
            </div>

            {/* RIGHT HALF — Date/Time top-right + Big Current Token (Login-page brand tones) */}
            <div
              className="w-1/2 flex flex-col px-6 py-6 min-w-0 relative"
              data-testid="tv-right-panel"
              style={{
                background: showGreenBlink
                  ? '#15803d'
                  : 'radial-gradient(circle at 80% 20%, rgba(22,163,74,0.10), transparent 45%), radial-gradient(circle at 20% 80%, rgba(30,58,138,0.10), transparent 50%), linear-gradient(135deg, #eff6ff 0%, #ecfdf5 100%)',
              }}
            >
              {/* Date & Time — centered, elegant brand-themed clock */}
              <div className={`text-center mb-1 ${showGreenBlink ? 'text-white' : ''}`} data-testid="tv-datetime">
                <div
                  className="font-black tabular-nums leading-none tracking-wider bg-clip-text"
                  style={{
                    fontFamily: 'Manrope, "Segoe UI", sans-serif',
                    fontSize: 'clamp(44px, 7vh, 96px)',
                    color: showGreenBlink ? '#ffffff' : 'transparent',
                    backgroundImage: showGreenBlink ? undefined : 'linear-gradient(135deg, #1e3a8a 0%, #16a34a 100%)',
                    WebkitBackgroundClip: showGreenBlink ? undefined : 'text',
                    textShadow: showGreenBlink ? '0 3px 8px rgba(0,0,0,0.3)' : 'none',
                    filter: showGreenBlink ? 'none' : 'drop-shadow(0 4px 10px rgba(30,58,138,0.18))',
                    letterSpacing: '0.05em',
                  }}
                >
                  {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}
                </div>
                <div className="flex items-center justify-center gap-2 mt-2">
                  <span aria-hidden className="inline-block h-px w-8" style={{ background: showGreenBlink ? 'rgba(255,255,255,0.6)' : 'linear-gradient(90deg, transparent, rgba(30,58,138,0.6))' }} />
                  <span
                    className={`font-semibold tracking-[0.18em] uppercase ${showGreenBlink ? 'text-white/95' : ''}`}
                    style={{
                      color: showGreenBlink ? undefined : '#1e3a8a',
                      fontSize: 'clamp(14px, 1.9vh, 22px)',
                      fontFamily: 'Manrope, sans-serif',
                    }}
                  >
                    {currentTime.toLocaleDateString('en-IN', { weekday: 'long' })}
                  </span>
                  <span aria-hidden className="inline-block h-px w-8" style={{ background: showGreenBlink ? 'rgba(255,255,255,0.6)' : 'linear-gradient(90deg, rgba(22,163,74,0.6), transparent)' }} />
                </div>
                <div
                  className={`font-medium mt-1 ${showGreenBlink ? 'text-white/90' : ''}`}
                  style={{ color: showGreenBlink ? undefined : '#16a34a', fontSize: 'clamp(13px, 1.7vh, 20px)' }}
                >
                  {currentTime.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                </div>
              </div>

              {/* Token block — middle of right half */}
              <div className="flex-1 flex flex-col items-center justify-center min-h-0 relative">
                {currentPatient ? (
                  <>
                    {/* BIG paediatric symbol behind the token when calling a paeds patient */}
                    {(currentPatient.token_number || '').toUpperCase().startsWith('P') && (
                      <div
                        aria-hidden
                        className="absolute inset-0 flex items-center justify-center pointer-events-none"
                        data-testid="tv-paeds-marker"
                        style={{ animation: isBlinking ? 'paeds-bounce 0.6s ease-in-out' : 'paeds-float 4s ease-in-out infinite' }}
                      >
                        <Baby
                          style={{
                            width: 'clamp(280px, 60vh, 620px)',
                            height: 'clamp(280px, 60vh, 620px)',
                            color: showGreenBlink ? 'rgba(255,255,255,0.18)' : 'rgba(236,72,153,0.16)',
                            filter: 'drop-shadow(0 12px 30px rgba(236,72,153,0.30))',
                          }}
                          strokeWidth={1.25}
                        />
                      </div>
                    )}
                    <p
                      className={`font-bold tracking-[0.3em] uppercase relative ${showGreenBlink ? 'text-white' : ''}`}
                      style={{
                        fontSize: 'clamp(22px, 3.4vh, 44px)',
                        color: showGreenBlink
                          ? undefined
                          : ((currentPatient.token_number || '').toUpperCase().startsWith('P') ? '#be185d' : '#16a34a'),
                      }}
                    >
                      {(currentPatient.token_number || '').toUpperCase().startsWith('P') ? '👶 Now Serving Child' : 'Now Serving'}
                    </p>
                    <div
                      className="font-black leading-none my-3 bg-clip-text relative"
                      style={{
                        fontSize: 'clamp(110px, 26vh, 240px)',
                        fontFamily: 'Arial Black, Arial, sans-serif',
                        letterSpacing: '-0.04em',
                        color: showGreenBlink ? '#ffffff' : 'transparent',
                        backgroundImage: showGreenBlink
                          ? undefined
                          : ((currentPatient.token_number || '').toUpperCase().startsWith('P')
                              ? 'linear-gradient(135deg, #ec4899 0%, #be185d 50%, #1e3a8a 100%)'
                              : 'linear-gradient(135deg, #1e3a8a 0%, #16a34a 100%)'),
                        WebkitBackgroundClip: showGreenBlink ? undefined : 'text',
                        textShadow: showGreenBlink ? '0 8px 20px rgba(0,0,0,0.35)' : 'none',
                        filter: showGreenBlink
                          ? 'none'
                          : ((currentPatient.token_number || '').toUpperCase().startsWith('P')
                              ? 'drop-shadow(0 6px 18px rgba(236,72,153,0.35))'
                              : 'drop-shadow(0 6px 16px rgba(30,58,138,0.30))'),
                        animation: isBlinking ? 'token-flash 0.4s ease-in-out' : 'none',
                      }}
                      data-testid="tv-current-token"
                    >
                      {currentPatient.token_number}
                    </div>

                    {currentPatient.name && (
                      <p
                        className={`font-semibold tracking-wide truncate max-w-full px-4 ${showGreenBlink ? 'text-white/95' : 'text-slate-700'}`}
                        style={{ fontSize: 'clamp(22px, 3vh, 38px)' }}
                      >
                        {currentPatient.name}
                      </p>
                    )}

                    <div className="flex items-center justify-center gap-2 flex-wrap mt-4">
                      {currentPatient.urgent && (
                        <div className="flex items-center gap-1.5 bg-red-600 text-white px-3 py-1 rounded-full shadow">
                          <AlertTriangle className="h-4 w-4" />
                          <span className="text-base font-bold">URGENT</span>
                        </div>
                      )}
                      {currentPatient.fever && (
                        <div className="flex items-center gap-1.5 bg-orange-500 text-white px-3 py-1 rounded-full shadow">
                          <Thermometer className="h-4 w-4" />
                          <span className="text-base font-bold">FEVER</span>
                        </div>
                      )}
                      {isChild(currentPatient.age) && (
                        <div className="flex items-center gap-1.5 bg-pink-500 text-white px-3 py-1 rounded-full shadow">
                          <Baby className="h-4 w-4" />
                          <span className="text-base font-bold">CHILD</span>
                        </div>
                      )}
                      {currentPatient.review && (
                        <div className="flex items-center gap-1.5 bg-blue-600 text-white px-3 py-1 rounded-full shadow">
                          <FileText className="h-4 w-4" />
                          <span className="text-base font-bold">REVIEW</span>
                        </div>
                      )}
                      {currentPatient.diabetes && (
                        <div className="flex items-center gap-1.5 bg-purple-600 text-white px-3 py-1 rounded-full shadow">
                          <Activity className="h-4 w-4" />
                          <span className="text-base font-bold">DM</span>
                        </div>
                      )}
                      {currentPatient.htn && (
                        <div className="flex items-center gap-1.5 bg-rose-600 text-white px-3 py-1 rounded-full shadow">
                          <Heart className="h-4 w-4" />
                          <span className="text-base font-bold">HTN</span>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="text-center" data-testid="tv-no-patient">
                    <p className={`font-bold uppercase tracking-widest ${showGreenBlink ? 'text-white/90' : 'text-slate-400'}`} style={{ fontSize: 'clamp(32px, 4.4vh, 60px)' }}>
                      No Patient
                    </p>
                    <p className={`mt-3 ${showGreenBlink ? 'text-white/80' : 'text-slate-400'}`} style={{ fontSize: 'clamp(20px, 2.6vh, 32px)' }}>
                      Waiting for next token…
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* LOWER SEGMENT - Hidden during promo */}
      {!(showPromo && currentPromo) && (
      <div
        className="text-white py-3 relative"
        style={{
          background: 'linear-gradient(90deg, #1e3a8a 0%, #1e40af 50%, #16a34a 100%)',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.15), 0 -2px 12px rgba(30,58,138,0.25)',
        }}
      >
        {/* Brand edge accent */}
        <div aria-hidden className="absolute inset-x-0 top-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(167,243,208,0.7), transparent)' }} />
        {/* Line 1 */}
        <div className="overflow-hidden whitespace-nowrap mb-1">
          {scroll1Enabled ? (
            <div
              className="inline-block text-2xl font-semibold"
              style={{
                color: '#fde68a',
                animation: 'marquee 45s linear infinite',
                paddingLeft: '100%',
                textShadow: '0 2px 4px rgba(0,0,0,0.25)',
              }}
            >
              {isClosingTime
                ? '⚠️ OPD WILL BE CLOSED BY 10 PM ⚠️ • ' + announcement1 + ' • ⚠️ OPD WILL BE CLOSED BY 10 PM ⚠️ • ' + announcement1 + ' • '
                : announcement1 + ' • ' + announcement1 + ' • ' + announcement1 + ' • '
              }
            </div>
          ) : (
            <p className="text-2xl font-semibold text-center" style={{ color: '#fde68a' }}>
              {isClosingTime ? '⚠️ OPD WILL BE CLOSED BY 10 PM ⚠️' : announcement1}
            </p>
          )}
        </div>

        {/* Line 2 */}
        <div className="overflow-hidden whitespace-nowrap border-t pt-1" style={{ borderColor: 'rgba(255,255,255,0.18)' }}>
          {scroll2Enabled ? (
            <div
              className="inline-block text-xl"
              style={{
                color: '#a7f3d0',
                animation: 'marquee-reverse 50s linear infinite',
                paddingLeft: '100%',
              }}
            >
              {announcement2} • {announcement2} • {announcement2} • {announcement2} •
            </div>
          ) : (
            <p className="text-xl text-center" style={{ color: '#a7f3d0' }}>{announcement2}</p>
          )}
        </div>
      </div>
      )}

      {/* CSS for marquee animation and fading the Emergent badge */}
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0%); }
          100% { transform: translateX(-50%); }
        }
        @keyframes marquee-reverse {
          0% { transform: translateX(-50%); }
          100% { transform: translateX(0%); }
        }
        @keyframes serving-glow {
          0%, 100% { box-shadow: 0 0 30px 8px rgba(250, 204, 21, 0.55), 0 0 60px 16px rgba(250, 204, 21, 0.35), inset 0 0 0 4px rgba(250, 204, 21, 0.9); }
          50%      { box-shadow: 0 0 50px 14px rgba(254, 240, 138, 0.75), 0 0 90px 28px rgba(250, 204, 21, 0.5), inset 0 0 0 4px rgba(254, 240, 138, 1); }
        }
        @keyframes token-flash {
          0%, 100% { text-shadow: 0 0 18px rgba(255,255,255,0.85), 0 6px 12px rgba(0,0,0,0.5); transform: scale(1); }
          50%      { text-shadow: 0 0 32px rgba(255,255,255,1),    0 6px 12px rgba(0,0,0,0.5); transform: scale(1.02); }
        }
        @keyframes next-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(251, 146, 60, 0.7), 0 12px 30px rgba(0,0,0,0.25); }
          50%      { box-shadow: 0 0 0 14px rgba(251, 146, 60, 0),  0 12px 30px rgba(0,0,0,0.25); }
        }
        @keyframes badge-bounce {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-3px); }
        }
        @keyframes live-dot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%      { opacity: 0.45; transform: scale(0.85); }
        }
        /* Hide the Emergent badge on TV Display */
        #emergent-badge,
        [data-emergent-badge],
        .emergent-badge,
        div[class*="emergent"],
        a[href*="emergent.sh"],
        div[id*="emergent"] {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          pointer-events: none !important;
        }
      `}</style>

      {/* Invisible muted looping video — keeps Android WebView / Silk screen awake when Wake Lock API isn't available */}
      <video
        ref={noSleepVideoRef}
        muted
        playsInline
        loop
        autoPlay
        aria-hidden="true"
        tabIndex={-1}
        style={{
          position: 'fixed',
          width: '1px',
          height: '1px',
          opacity: 0.01,
          bottom: 0,
          right: 0,
          pointerEvents: 'none',
          zIndex: -1,
        }}
      />
    </div>
  );
};

export default TVDisplay;
